import { Button } from "@chakra-ui/button";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/modal";
import { FormControl, FormLabel, Input, Text } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";

interface ConfirmationWithInputDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmationText: string;
  inputLabel: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  isLoading?: boolean;
}

export const ConfirmationWithInputDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmationText,
  inputLabel,
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  isLoading = false,
}: ConfirmationWithInputDialogProps) => {
  const [inputValue, setInputValue] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isConfirmDisabled = inputValue !== confirmationText || isLoading;

  useEffect(() => {
    if (isOpen) {
      setInputValue("");
      // Focus the input when dialog opens
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!isConfirmDisabled) {
      onConfirm();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !isConfirmDisabled) {
      handleConfirm();
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{title}</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Text mb={4}>{message}</Text>
            <FormControl>
              <FormLabel>
                {inputLabel}
                <Text as="span" fontWeight="bold" ml={1}>
                  {confirmationText}
                </Text>
              </FormLabel>
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Type "${confirmationText}" to confirm`}
                isDisabled={isLoading}
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              {cancelButtonText}
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={handleConfirm}
              isDisabled={isConfirmDisabled}
              isLoading={isLoading}
            >
              {confirmButtonText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
